import { Component, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmDialogComponent,
    LoadingSlateComponent,
    StatusTagComponent,
    FloatingToolbarComponent,
    PagingComponent,
} from '@exl-ng/mulo-common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PaginationInstance, NgxPaginationModule } from 'ngx-pagination';
import { Subscription, finalize } from 'rxjs';
import { Profile } from '../../shared/configurations/profile.constant';
import { PaginationUtils } from '../../shared/utils/pagination.utils';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { SEOService } from '../../core/seo.service';
import { MediaMention } from '../../shared/interfaces/media-mention.interface';
import { ProfileDataService } from '../profile-data.service';
import { ProfileService } from '../profile.service';
import { ProfileMentionsService } from './profile-mentions.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { MatIcon } from '@angular/material/icon';
import { CitationBriefComponent } from '../../parts/citations/citation-brief/citation-brief.component';

import { CenteredBlockLayoutComponent } from '@exl-ng/mulo-views';

@Component({
    selector: 'esp-profile-mentions',
    templateUrl: './profile-mentions.component.html',
    styleUrls: ['./profile-mentions.component.scss'],
    imports: [
        CenteredBlockLayoutComponent,
        LoadingSlateComponent,
        CitationBriefComponent,
        TranslateModule,
        StatusTagComponent,
        MatIcon,
        SvgViewboxDirective,
        MatTooltip,
        FloatingToolbarComponent,
        NgxPaginationModule,
        PagingComponent,
    ],
})
export class ProfileMentionsComponent implements OnInit {
    private matDialog = inject(MatDialog);
    private profileMentionsService = inject(ProfileMentionsService);
    profileDataService = inject(ProfileDataService);
    private SEOService = inject(SEOService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private translate = inject(TranslateService);
    profileService = inject(ProfileService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    mentions: MediaMention[] = [];
    isLoading = false;
    mainInstName: string;
    nameSubscription: Subscription;

    hideMenu = [{ name: 'research.profile.hide.action.hide' }];
    displayMenu = [{ name: 'research.profile.hide.action.display' }];
    emptyMenu = [];

    paginationConfig: PaginationInstance = {
        id: 'serverPagingMedia',
        itemsPerPage: Profile.BULK_SIZE,
        currentPage: 1,
    };

    pagesCount: number;

    trackByIndex = (index: number, mention: MediaMention) => index;

    ngOnInit(): void {
        let page = '';
        if (this.route.snapshot.queryParamMap) {
            page = this.route.snapshot.queryParamMap.get('page');
        }
        this.route.data.subscribe((data) => {
            if (page) {
                this.paginationConfig.currentPage = page as unknown as number;
            } else {
                this.paginationConfig.currentPage = 1;
            }
            this.getMediaMentions();
        });

        this.seoSupport();
    }

    getMediaMentions() {
        this.isLoading = true;
        this.profileMentionsService
            .getMediaMentions(
                PaginationUtils.searchOffset(this.paginationConfig),
            )
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe((data) => {
                this.mentions = data.results as MediaMention[];
                this.paginationConfig.totalItems = data.count;
                this.pagesCount = PaginationUtils.calcPagesCount(
                    this.paginationConfig,
                );
            });
    }

    seoSupport() {
        this.mainInstName =
            this.configurationHandlerService.mainInstitutionName();
        //deep link to the direct tab - need to wait until the name updates
        this.nameSubscription = this.profileService.nameChange$.subscribe(
            (res) => {
                this.changeDocumentTitle(res);
            },
        );
        //moving between tabs
        if (this.profileDataService.displayName.value) {
            this.changeDocumentTitle(this.profileDataService.displayName.value);
        }
    }

    changeDocumentTitle(userName) {
        const code = this.getDocumentTitle();
        this.translate
            .get(code, {
                value0: userName,
                value1: this.mainInstName,
            })
            .subscribe((res) => {
                this.SEOService.updateTitle(res);
            });
    }

    getDocumentTitle() {
        let code = '';
        if (this.profileService.isPublicPage) {
            code = 'research.public.profile.media.tab.document.title';
        } else {
            code = 'research.profile.media.tab.document.title';
        }
        return code;
    }

    getMenuAction(mention: MediaMention) {
        if (this.profileService.isPublicPage) {
            return this.emptyMenu;
        }
        if (mention.displayInPublicProfile) {
            return this.hideMenu;
        }
        return this.displayMenu;
    }

    onActionClick(action, mention, index) {
        if (action === 'research.profile.hide.action.hide') {
            this.hideMention(mention);
        } else if (action === 'research.profile.hide.action.display') {
            this.displayMention(mention);
        }
    }

    hideMention(mention: MediaMention) {
        this.showHideMentionDialog()
            .afterClosed()
            .subscribe((response) => {
                if (response === 'ok') {
                    this.profileMentionsService
                        .hideMentionFromPublicProfile(mention.entityId)
                        .subscribe(() => {
                            mention.displayInPublicProfile = false;
                        });
                }
            });
    }

    displayMention(mention: MediaMention) {
        this.profileMentionsService
            .displayMentionOnPublicProfile(mention.entityId)
            .subscribe(() => {
                mention.displayInPublicProfile = true;
            });
    }

    getByline(mention: MediaMention) {
        let byline;
        if (mention.source && mention.reporter) {
            byline = `${mention.source} (${mention.reporter})`;
        } else if (mention.source) {
            byline = mention.source;
        } else if (mention.reporter) {
            byline = mention.reporter;
        }
        return byline;
    }

    showHideMentionDialog() {
        // using t for translate function to increase readability
        const t = (code) => this.translate.instant(code);

        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: t('research.profile.hide.confirmation.title'),
                message: t('research.profile.hide.confirmation.message'),
                actions: {
                    cancel: t('research.profile.hide.confirmation.cancel'),
                    confirm: t('research.profile.hide.confirmation.confirm'),
                },
                actionFocus: 'confirm',
            },
        });
        return dialogRef;
    }

    pageChange(event) {
        this.paginationConfig.currentPage = event;
        this.getMediaMentions();
        this.addPageParamToURL();
    }

    addPageParamToURL() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page: this.paginationConfig.currentPage },
            queryParamsHandling: 'merge',
        });
    }
}
